import { AnimatePresence, motion } from 'motion/react';

interface Props {
  /** What is counted: the picks while choosing, the hits once the draw starts. */
  label: string;
  value: number;
  total: number;
  /** Counting hits: each new one pops the number and floats a +1. */
  hits: boolean;
  /** The round settled as a win. */
  won: boolean;
  reduced: boolean;
}

/**
 * The board's big readout: how many picked, then how many hit. It is a control surface, so
 * it wears the site's theme (surfaces, text, primary), never the game's art colours.
 */
export function KenoCounter({ label, value, total, hits, won, reduced }: Props) {
  const lit = hits && value > 0;
  return (
    <div
      data-keno-counter={hits ? 'hits' : 'picks'}
      data-won={won ? 'true' : 'false'}
      className={[
        'relative flex items-baseline gap-1.5 rounded-lg bg-[var(--bg-elevated)] px-3 py-1 transition-shadow duration-300',
        won ? 'shadow-[0_0_0_2px_var(--primary),0_0_18px_var(--primary)]' : '',
      ].join(' ')}
    >
      <span className="self-center text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</span>
      <motion.span
        key={hits ? `h${value}` : 'p'}
        initial={lit && !reduced ? { scale: 1.6 } : false}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 520, damping: 14 }}
        className={[
          'inline-block min-w-[1ch] text-center text-[24px] font-black leading-none tabular-nums',
          lit ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
        ].join(' ')}
      >
        {value}
      </motion.span>
      <span className="text-[13px] font-bold tabular-nums text-[var(--text-secondary)]">/ {total}</span>
      <AnimatePresence>
        {lit && !reduced && (
          <motion.span
            key={value}
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-0 text-[13px] font-black text-[var(--primary)]"
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 0, y: -22 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            +1
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
}
